import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight,
  Scale,
  TrendingDown,
  FileCheck,
  Calculator,
  CheckCircle,
  ArrowDown,
  Percent,
  Calendar,
  Shield
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';

const GestaoPassivos = () => {
  const { t } = useLanguage();

  const processSteps = [
    { step: 1, titleKey: 'gpPage.process1', descKey: 'gpPage.process1Desc' },
    { step: 2, titleKey: 'gpPage.process2', descKey: 'gpPage.process2Desc' },
    { step: 3, titleKey: 'gpPage.process3', descKey: 'gpPage.process3Desc' },
    { step: 4, titleKey: 'gpPage.process4', descKey: 'gpPage.process4Desc' },
  ];

  const ratingDiscounts = [
    { rating: 'A', discount: '0%', descKey: 'gpPage.ratingA', color: 'stone' },
    { rating: 'B', discount: '30%', descKey: 'gpPage.ratingB', color: 'bronze' },
    { rating: 'C', discount: '50%', descKey: 'gpPage.ratingC', color: 'emerald' },
    { rating: 'D', discount: '70%', descKey: 'gpPage.ratingD', color: 'emerald' },
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="gestao-passivos-page">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-stone-50 noise-bg">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-bronze font-medium text-sm tracking-wider uppercase mb-4 block">
                {t('gpPage.heroTag')}
              </span>
              <h1 className="text-4xl md:text-5xl font-serif font-bold text-stone-900 mb-6">
                {t('gpPage.heroTitle')}{' '}
                <span className="gold-sheen">{t('gpPage.heroHighlight')}</span>
              </h1>
              <p className="text-lg text-stone-600 mb-8 leading-relaxed">
                {t('gpPage.heroDesc')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  asChild
                  className="bg-emerald hover:bg-emerald-light text-white rounded-sm px-8 py-6 text-lg font-serif btn-shine"
                  data-testid="gp-cta-primary"
                >
                  <Link to="/contato" className="flex items-center gap-2">
                    {t('gpPage.ctaPrimary')}
                    <ArrowRight className="h-5 w-5" />
                  </Link>
                </Button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="hidden lg:block"
            >
              <div className="relative">
                <div className="absolute -inset-4 bg-emerald/10 rounded-sm blur-2xl" />
                <img 
                  src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&q=80"
                  alt="Tax document analysis"
                  className="relative rounded-sm shadow-2xl w-full"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Case Study Highlight */}
      <section className="py-16 bg-emerald" data-testid="case-study">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-8">
            <span className="text-bronze font-medium text-sm tracking-wider uppercase">
              {t('gpPage.caseStudyTag')}
            </span>
          </div>
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-stone-400 text-sm mb-2">{t('gpPage.caseInitialDebt')}</p>
              <p className="text-3xl md:text-4xl font-serif font-bold text-white">R$ 1,18 mi</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <p className="text-stone-400 text-sm mb-2">{t('gpPage.caseInitialRating')}</p>
              <p className="text-3xl md:text-4xl font-serif font-bold text-white">A</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <p className="text-stone-400 text-sm mb-2">{t('gpPage.caseDiscount')}</p>
              <p className="text-3xl md:text-4xl font-serif font-bold text-bronze">48%</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <p className="text-stone-400 text-sm mb-2">{t('gpPage.caseFinalValue')}</p>
              <p className="text-3xl md:text-4xl font-serif font-bold text-white">R$ 618 mil</p>
            </motion.div>
          </div>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mt-8"
          >
            <p className="text-stone-300">
              {t('gpPage.caseInstallments')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Rating System */}
      <section className="py-24 md:py-32 bg-white" data-testid="rating-system">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-bronze font-medium text-sm tracking-wider uppercase mb-4 block">
              {t('gpPage.ratingTag')}
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-stone-900 mb-4">
              {t('gpPage.ratingTitle')}
            </h2>
            <p className="text-stone-600 max-w-2xl mx-auto">
              {t('gpPage.ratingDesc')}
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {ratingDiscounts.map((item, index) => (
              <motion.div
                key={item.rating}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`text-center p-8 rounded-sm ${
                  item.color === 'emerald' 
                    ? 'bg-emerald text-white' 
                    : item.color === 'bronze'
                    ? 'bg-bronze/10 border border-bronze/30'
                    : 'bg-stone-100'
                }`}
                data-testid={`rating-${item.rating}`}
              >
                <p className={`text-6xl font-serif font-bold mb-2 ${
                  item.color === 'emerald' ? 'text-white' : 'text-stone-900'
                }`}>
                  {item.rating}
                </p>
                <p className={`text-3xl font-serif font-bold mb-2 ${
                  item.color === 'emerald' ? 'text-bronze' : 'text-bronze'
                }`}>
                  {item.discount}
                </p>
                <p className={`text-sm ${
                  item.color === 'emerald' ? 'text-stone-300' : 'text-stone-600'
                }`}>
                  {t(item.descKey)}
                </p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="flex justify-center items-center gap-4 mt-8"
          >
            <ArrowDown className="h-6 w-6 text-emerald animate-bounce" />
            <p className="text-stone-600 font-medium">
              {t('gpPage.installmentsInfo')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 md:py-32 bg-stone-50 noise-bg" data-testid="gp-process">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-bronze font-medium text-sm tracking-wider uppercase mb-4 block">
              {t('gpPage.processTag')}
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-stone-900">
              {t('gpPage.processTitle')}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <motion.div
                key={step.titleKey}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative"
                data-testid={`gp-step-${index}`}
              >
                {index < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-8 left-1/2 w-full h-0.5 bg-stone-200" />
                )}
                <div className="relative text-center">
                  <div className="w-16 h-16 rounded-full bg-emerald text-white flex items-center justify-center mx-auto mb-4 relative z-10 text-xl font-serif font-bold">
                    {step.step}
                  </div>
                  <h3 className="text-lg font-serif font-semibold text-stone-900 mb-2">
                    {t(step.titleKey)}
                  </h3>
                  <p className="text-stone-600 text-sm">
                    {t(step.descKey)}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 md:py-32 bg-emerald text-white" data-testid="gp-benefits">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-bronze font-medium text-sm tracking-wider uppercase mb-4 block">
                {t('gpPage.benefitsTag')}
              </span>
              <h2 className="text-3xl md:text-4xl font-serif font-bold mb-6">
                {t('gpPage.benefitsTitle')}
              </h2>
              <ul className="space-y-4">
                {[
                  { icon: <TrendingDown className="h-5 w-5" />, textKey: 'gpPage.benefit1' },
                  { icon: <Calendar className="h-5 w-5" />, textKey: 'gpPage.benefit2' },
                  { icon: <Calculator className="h-5 w-5" />, textKey: 'gpPage.benefit3' },
                  { icon: <Shield className="h-5 w-5" />, textKey: 'gpPage.benefit4' },
                  { icon: <CheckCircle className="h-5 w-5" />, textKey: 'gpPage.benefit5' },
                ].map((item) => (
                  <li key={item.textKey} className="flex items-center gap-3 bg-white/10 p-4 rounded-sm">
                    <span className="text-bronze">{item.icon}</span>
                    <span className="text-stone-200">{t(item.textKey)}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-white/10 backdrop-blur-sm p-8 rounded-sm">
                <Scale className="h-12 w-12 text-bronze mb-6" />
                <h3 className="text-2xl font-serif font-bold mb-4">
                  {t('gpPage.legalBasisTitle')}
                </h3>
                <p className="text-stone-300 mb-6">
                  {t('gpPage.legalBasisDesc')}
                </p>
                <div className="bg-white/10 p-4 rounded-sm">
                  <p className="text-sm text-stone-300">
                    {t('gpPage.legalBasisNote')}
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="py-24 md:py-32 bg-white" data-testid="gp-comparison">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-bronze font-medium text-sm tracking-wider uppercase mb-4 block">
              {t('gpPage.comparisonTag')}
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-stone-900">
              {t('gpPage.comparisonTitle')}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-stone-100 p-8 rounded-sm"
            >
              <h3 className="text-xl font-serif font-semibold text-stone-900 mb-6">
                {t('gpPage.withoutTitle')}
              </h3>
              <ul className="space-y-4">
                {[
                  'gpPage.without1',
                  'gpPage.without2',
                  'gpPage.without3',
                  'gpPage.without4',
                  'gpPage.without5',
                ].map((itemKey) => (
                  <li key={itemKey} className="flex items-center gap-3 text-stone-600">
                    <span className="w-2 h-2 rounded-full bg-red-500" />
                    {t(itemKey)}
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-emerald p-8 rounded-sm text-white"
            >
              <h3 className="text-xl font-serif font-semibold mb-6">
                {t('gpPage.withTitle')}
              </h3>
              <ul className="space-y-4">
                {[
                  'gpPage.with1',
                  'gpPage.with2',
                  'gpPage.with3',
                  'gpPage.with4',
                  'gpPage.with5',
                ].map((itemKey) => (
                  <li key={itemKey} className="flex items-center gap-3 text-stone-200">
                    <CheckCircle className="h-5 w-5 text-bronze flex-shrink-0" />
                    {t(itemKey)}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-stone-50 noise-bg">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Percent className="h-16 w-16 text-bronze mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-stone-900 mb-6">
              {t('gpPage.ctaTitle')}
            </h2>
            <p className="text-stone-600 text-lg mb-8">
              {t('gpPage.ctaDesc')}
            </p>
            <Button 
              asChild
              className="bg-emerald hover:bg-emerald-light text-white rounded-sm px-8 py-6 text-lg font-serif btn-shine"
              data-testid="gp-final-cta"
            >
              <Link to="/contato" className="flex items-center gap-2">
                {t('gpPage.ctaBtn')}
                <ArrowRight className="h-5 w-5" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default GestaoPassivos;
